import { ServicesURL } from "@utils/utilsApi";
import { fetcher } from "src/services/config";
import { MethodType } from "src/services/type";
import { mapAggregatedType } from "../../types";
import {
  EnrollmentProgressTrigger,
  ProgressNode,
  ProgressRootFields,
} from "../types";

const CHILDREN_PAGE_SIZE = 200;

const toNumberOrNull = (value: unknown): number | null => {
  if (value === undefined || value === null || value === "") return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

const extractChildren = (response: any): any[] =>
  response?.output ??
  response?.pathChildrenList ??
  response?.courseChildrenList ??
  (Array.isArray(response) ? response : []);

const mapRawToNode = (raw: any, parent: ProgressNode): ProgressNode => {
  const id = Number(raw.id ?? raw.activity_id);
  const activityId = Number(raw.activity_id ?? raw.id);
  return {
    key: `${parent.key}/${id}`,
    id,
    activityId,
    entityType: mapAggregatedType(raw.type),
    title: raw.title || raw.name || raw.activity_name || `ID ${id}`,
    category: raw.category,
    status: raw.status ?? null,
    completeDate: raw.completeDate ?? raw.complete_date ?? null,
    progPerc: raw.progPerc ?? raw.progr_perc ?? raw.prog_perc ?? null,
    learningObjectId: toNumberOrNull(raw.id_learning_object),
    parentActivityId:
      toNumberOrNull(raw.parent_activity_id) ??
      (parent.entityType === "activity" ? null : parent.activityId),
    grandparentActivityId:
      toNumberOrNull(raw.grandparent_activity_id) ??
      parent.parentActivityId ??
      null,
    parentEntityType: parent.entityType,
    isTest: raw.test === true,
    forceable: raw.forceability?.forceable !== false,
    forceableFromParent: raw.forceability?.forceableFromParent !== false,
    children: [],
  };
};

const childrenUrl = (
  corporateId: string | number,
  userId: string | number,
  node: ProgressNode,
): string | null => {
  const base =
    node.entityType === "path"
      ? ServicesURL.getPathChildren.replace(":idPath", String(node.id))
      : node.entityType === "course"
        ? ServicesURL.getCourseChildren.replace(":idCourse", String(node.id))
        : null;
  if (!base) return null;
  const params = new URLSearchParams({
    student_id: String(userId),
    pageIndex: "0",
    pageSize: String(CHILDREN_PAGE_SIZE),
  });
  if (node.entityType === "course" && node.parentActivityId != null) {
    params.set("path_id", String(node.parentActivityId));
  }
  return `${base.replace(":idCorporate", String(corporateId))}?${params.toString()}`;
};

const loadChildren = async (
  corporateId: string | number,
  userId: string | number,
  node: ProgressNode,
): Promise<ProgressNode> => {
  const url = childrenUrl(corporateId, userId, node);
  if (!url) return node;
  const response = await fetcher<any>(url, MethodType.GET);
  const children = extractChildren(response).map((raw) =>
    mapRawToNode(raw, node),
  );
  return {
    ...node,
    children: await Promise.all(
      children.map((child) => loadChildren(corporateId, userId, child)),
    ),
  };
};

export const buildRootNode = (
  trigger: EnrollmentProgressTrigger,
): ProgressNode => ({
  key: String(trigger.objectId),
  id: trigger.objectId,
  activityId: trigger.activityId ?? trigger.objectId,
  entityType: trigger.objectType,
  title: trigger.objectTitle || `ID ${trigger.objectId}`,
  category: trigger.category,
  status: trigger.status ?? null,
  completeDate: trigger.completeDate ?? null,
  progPerc: trigger.progPerc ?? null,
  learningObjectId: trigger.learningObjectId ?? null,
  parentActivityId: null,
  grandparentActivityId: null,
  parentEntityType: null,
  isTest: false,
  forceable: trigger.forceable !== false,
  forceableFromParent: true,
  children: [],
});

export const fetchProgressTree = (
  trigger: EnrollmentProgressTrigger,
): Promise<ProgressNode> =>
  loadChildren(trigger.corporateId, trigger.userId, buildRootNode(trigger));

export const buildRootEnrollmentUrl = (
  trigger: EnrollmentProgressTrigger,
): string => {
  const params = new URLSearchParams({
    page: "0",
    page_size: "1",
    lo_id: String(trigger.objectId),
  });
  const base = ServicesURL.getLOBasedOnStudentId
    .replace(":idCorporate", String(trigger.corporateId))
    .replace(":studentId", String(trigger.userId));
  return `${base}?${params.toString()}`;
};

export const fetchRootEnrollment = async (
  trigger: EnrollmentProgressTrigger,
): Promise<ProgressRootFields | null> => {
  const response = await fetcher<any>(
    buildRootEnrollmentUrl(trigger),
    MethodType.GET,
  );
  const rows: any[] = Array.isArray(response?.output) ? response.output : [];
  const row = rows.find(
    (raw) => Number(raw?.activity_id ?? raw?.id) === Number(trigger.objectId),
  );
  if (!row) return null;
  return {
    status: row.status ?? null,
    completeDate: (row.completeDate ?? row.complete_date) || null,
    progPerc: toNumberOrNull(row.progPerc ?? row.progr_perc ?? row.prog_perc),
    forceable: row.forceable !== false,
  };
};
